import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons,
  IonBackButton, IonButton, IonSpinner, IonIcon,
} from '@ionic/react';
import { cameraOutline, checkmarkOutline } from 'ionicons/icons';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { useHistory, useParams } from 'react-router-dom';
import { usePreferredGames } from '../hooks/useGames';
import { useCard, useCards, useCreateCard, useUpdateCard } from '../hooks/useCards';
import { useCreateEvent } from '../hooks/useInventory';
import { cardService } from '../services/cardService';
import { API_BASE } from '../config/api';

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, fontSize: 14,
  fontWeight: 600, color: 'var(--ion-color-medium)',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', fontSize: 16,
  border: '1px solid var(--ion-color-medium)', borderRadius: 8,
  background: 'var(--ion-item-background, #fff)',
  color: 'var(--ion-text-color)',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = { ...inputStyle, appearance: 'auto' as any };

const fieldGroup: React.CSSProperties = { marginBottom: 12 };

const dataUrlToFile = async (dataUrl: string, filename: string): Promise<File> => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || 'image/jpeg' });
};

const ImageUploadSection: React.FC<{
  label: string;
  imageUrl: string;
  onUploaded: (url: string) => void;
  onRemove: () => void;
}> = ({ label, imageUrl, onUploaded, onRemove }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const url = await cardService.uploadImage(file);
      onUploaded(url);
    } catch {
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleTap = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        const perms = await Camera.requestPermissions({ permissions: ['camera', 'photos'] });
        console.log('Camera permissions:', perms);

        const photo = await Camera.getPhoto({
          quality: 85,
          allowEditing: false,
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Prompt,
          width: 1200,
          correctOrientation: true,
          promptLabelHeader: 'Select Image Source',
          promptLabelPhoto: 'Choose from Gallery',
          promptLabelPicture: 'Take Photo',
          promptLabelCancel: 'Cancel',
        });
        if (photo.dataUrl) {
          const file = await dataUrlToFile(photo.dataUrl, `card-${Date.now()}.jpg`);
          await uploadFile(file);
        }
      } catch (err: any) {
        console.error('Camera error:', err);
        // User cancelled - ignore
        if (err?.message?.includes('cancel') || err?.message?.includes('Cancel') || err?.message?.includes('User')) return;
        alert('Failed to capture image: ' + (err?.message || err));
      }
    } else {
      fileRef.current?.click();
    }
  };

  return (
    <div style={{ padding: '8px 0', flex: 1, minWidth: 0 }}>
      <label style={labelStyle}>{label}</label>
      {imageUrl ? (
        <div style={{ marginTop: 8 }}>
          <div style={{ position: 'relative' }}>
            <img
              src={imageUrl.startsWith('/uploads') ? `${API_BASE}${imageUrl}` : imageUrl}
              alt={label}
              style={{ width: '100%', aspectRatio: '5/7', objectFit: 'cover', borderRadius: 8, border: '1px solid var(--ion-color-light)', display: 'block' }}
            />
            <button
              onClick={onRemove}
              style={{
                position: 'absolute', top: -8, right: -8, width: 24, height: 24,
                borderRadius: '50%', border: 'none', background: 'var(--ion-color-danger)',
                color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 'bold',
              }}
            >X</button>
          </div>
          <IonButton expand="block" fill="outline" size="small" onClick={handleTap} disabled={uploading} style={{ marginTop: 8 }}>
            {uploading ? <IonSpinner name="crescent" /> : 'Replace'}
          </IonButton>
        </div>
      ) : (
        <div
          onClick={handleTap}
          style={{
            width: '100%', aspectRatio: '5/7', borderRadius: 8,
            border: '2px dashed var(--ion-color-medium)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: 12, color: 'var(--ion-color-medium)', textAlign: 'center',
            gap: 4, marginTop: 8,
          }}
        >
          {uploading ? <IonSpinner name="crescent" /> : (
            <>
              <IonIcon icon={cameraOutline} style={{ fontSize: 28 }} />
              Tap to add
            </>
          )}
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleFile}
      />
    </div>
  );
};

const MultiSelectDropdown: React.FC<{
  options: string[];
  value: string[];
  onChange: (selected: string[]) => void;
}> = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const toggle = (opt: string) => {
    onChange(
      value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt],
    );
  };

  const display = value.length > 0 ? value.join(', ') : 'Select...';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          ...inputStyle,
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: 42,
        }}
      >
        <span style={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
          color: value.length === 0 ? 'var(--ion-color-medium)' : undefined,
        }}>
          {display}
        </span>
        <span style={{ marginLeft: 8, fontSize: 12 }}>{open ? '\u25B2' : '\u25BC'}</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          background: 'var(--ion-item-background, #fff)',
          border: '1px solid var(--ion-color-medium)', borderRadius: 8,
          maxHeight: 220, overflowY: 'auto', marginTop: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {options.map((opt) => (
            <label
              key={opt}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', cursor: 'pointer',
                borderBottom: '1px solid var(--ion-color-light)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={value.includes(opt)}
                onChange={() => toggle(opt)}
                style={{ width: 18, height: 18, accentColor: 'var(--ion-color-primary)' }}
              />
              <span style={{ fontSize: 16 }}>{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const CardForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'new';
  const history = useHistory();
  const { data: games = [], isLoading: gamesLoading } = usePreferredGames();
  const { data: existingCard, isLoading: cardLoading } = useCard(isEdit ? Number(id) : 0);
  const createCard = useCreateCard();
  const updateCard = useUpdateCard();
  const createEvent = useCreateEvent();

  const { data: cardsData } = useCards({ per_page: 100 });
  const inventoryCards = cardsData?.items || [];

  const [isTrade, setIsTrade] = useState(false);
  const [tradedCardId, setTradedCardId] = useState<number | undefined>(undefined);
  const tradedCard = inventoryCards.find((c) => c.id === tradedCardId);

  const [form, setForm] = useState({
    game_id: 0,
    name: '',
    set_name: '',
    card_number: '',
    rarity: '',
    condition: '',
    quantity: 1,
    estimated_value: '' as string | number,
    purchase_price: '' as string | number,
    is_foil: false,
    is_first_edition: false,
    language: 'English',
    notes: '',
    image_front_url: '',
    image_back_url: '',
    custom_fields: {} as Record<string, any>,
  });

  useEffect(() => {
    if (isEdit && existingCard) {
      setForm({
        game_id: existingCard.game_id,
        name: existingCard.name,
        set_name: existingCard.set_name || '',
        card_number: existingCard.card_number || '',
        rarity: existingCard.rarity || '',
        condition: existingCard.condition || '',
        quantity: existingCard.quantity,
        estimated_value: existingCard.estimated_value ?? '',
        purchase_price: existingCard.purchase_price ?? '',
        is_foil: existingCard.is_foil,
        is_first_edition: existingCard.is_first_edition,
        language: existingCard.language,
        notes: existingCard.notes || '',
        image_front_url: existingCard.image_front_url || '',
        image_back_url: existingCard.image_back_url || '',
        custom_fields: existingCard.custom_fields || {},
      });
    }
  }, [isEdit, existingCard]);

  const selectedGame = games.find((g) => g.id === form.game_id);

  const handleSubmit = async () => {
    const payload = {
      ...form,
      estimated_value: form.estimated_value !== '' ? Number(form.estimated_value) : undefined,
      purchase_price: form.purchase_price !== '' ? Number(form.purchase_price) : undefined,
      image_front_url: form.image_front_url || undefined,
      image_back_url: form.image_back_url || undefined,
    };

    if (isEdit) {
      await updateCard.mutateAsync({ id: Number(id), data: payload });
    } else {
      await createCard.mutateAsync(payload);

      // If trade, log a trade event for the card given away
      if (isTrade && tradedCard) {
        await createEvent.mutateAsync({
          event_type: 'trade',
          card_id: tradedCard.id,
          card_name: tradedCard.name,
          game_name: tradedCard.game?.name || selectedGame?.name || '',
          quantity: form.quantity,
          value: tradedCard.estimated_value ?? undefined,
          trade_in_card_name: form.name,
          trade_in_value: form.estimated_value !== '' ? Number(form.estimated_value) : undefined,
        });
      }
    }
    history.goBack();
  };

  const loading = gamesLoading || (isEdit && cardLoading);

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar color="primary">
            <IonButtons slot="start"><IonBackButton defaultHref="/cards" /></IonButtons>
            <IonTitle>{isEdit ? 'Edit Card' : 'Add Card'}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding ion-text-center">
          <IonSpinner style={{ marginTop: 40 }} />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start"><IonBackButton defaultHref="/cards" /></IonButtons>
          <IonTitle>{isEdit ? 'Edit Card' : 'Add Card'}</IonTitle>
          {(createCard.isPending || updateCard.isPending) ? (
            <IonSpinner name="crescent" style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, color: '#fff', zIndex: 10 }} />
          ) : (
            <IonIcon
              icon={checkmarkOutline}
              onClick={() => {
                if (!form.name || !form.game_id || form.estimated_value === '' || createCard.isPending || updateCard.isPending) return;
                handleSubmit();
              }}
              style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 26, cursor: 'pointer', color: (!form.name || !form.game_id || form.estimated_value === '') ? 'rgba(255,255,255,0.4)' : '#fff', zIndex: 10 }}
            />
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div style={{ maxWidth: 500, margin: '0 auto' }}>
          <h3 style={{ marginTop: 0, marginBottom: 8 }}>Card Images</h3>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <ImageUploadSection
              label="Front"
              imageUrl={form.image_front_url}
              onUploaded={(url) => setForm((f) => ({ ...f, image_front_url: url }))}
              onRemove={() => setForm((f) => ({ ...f, image_front_url: '' }))}
            />
            <ImageUploadSection
              label="Back"
              imageUrl={form.image_back_url}
              onUploaded={(url) => setForm((f) => ({ ...f, image_back_url: url }))}
              onRemove={() => setForm((f) => ({ ...f, image_back_url: '' }))}
            />
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Game *</label>
            <select
              style={selectStyle}
              value={form.game_id || ''}
              onChange={(e) => setForm((f) => ({ ...f, game_id: Number(e.target.value), custom_fields: {} }))}
            >
              <option value="">Select a game</option>
              {games.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Card Name *</label>
            <input style={inputStyle} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Set Name</label>
            <input style={inputStyle} value={form.set_name} onChange={(e) => setForm((f) => ({ ...f, set_name: e.target.value }))} />
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Card Number</label>
            <input style={inputStyle} value={form.card_number} onChange={(e) => setForm((f) => ({ ...f, card_number: e.target.value }))} />
          </div>

          {selectedGame && (
            <>
              <div style={fieldGroup}>
                <label style={labelStyle}>Condition</label>
                <select style={selectStyle} value={form.condition} onChange={(e) => setForm((f) => ({ ...f, condition: e.target.value }))}>
                  <option value="">Select condition</option>
                  {selectedGame.condition_options.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div style={fieldGroup}>
                <label style={labelStyle}>Rarity</label>
                <select style={selectStyle} value={form.rarity} onChange={(e) => setForm((f) => ({ ...f, rarity: e.target.value }))}>
                  <option value="">Select rarity</option>
                  {selectedGame.rarity_options.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div style={fieldGroup}>
            <label style={labelStyle}>Quantity</label>
            <input style={inputStyle} type="number" min={1} value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: Number(e.target.value) || 1 }))} />
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Estimated Value ($) *</label>
            <input style={inputStyle} type="number" step="0.01" value={form.estimated_value} onChange={(e) => setForm((f) => ({ ...f, estimated_value: e.target.value }))} />
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Purchase Price ($)</label>
            <input style={inputStyle} type="number" step="0.01" value={form.purchase_price} onChange={(e) => setForm((f) => ({ ...f, purchase_price: e.target.value }))} />
          </div>

          <div style={{ ...fieldGroup, display: 'flex', gap: 24 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.is_foil} onChange={(e) => setForm((f) => ({ ...f, is_foil: e.target.checked }))} />
              Foil
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.is_first_edition} onChange={(e) => setForm((f) => ({ ...f, is_first_edition: e.target.checked }))} />
              First Edition
            </label>
          </div>

          {!isEdit && (
            <>
              <div style={{ ...fieldGroup, display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={isTrade} onChange={(e) => setIsTrade(e.target.checked)} />
                  This card was received in a trade
                </label>
              </div>

              {isTrade && (
                <div style={{ padding: 12, marginBottom: 12, border: '1px solid var(--ion-color-primary)', borderRadius: 8, background: 'var(--ion-color-primary-tint, rgba(56,128,255,0.05))' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: 'var(--ion-color-primary)' }}>Card Given Away</h4>
                  <div style={fieldGroup}>
                    <label style={labelStyle}>Select Card from Inventory *</label>
                    <select
                      style={selectStyle}
                      value={tradedCardId || ''}
                      onChange={(e) => setTradedCardId(e.target.value ? Number(e.target.value) : undefined)}
                    >
                      <option value="">Select a card...</option>
                      {inventoryCards.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.game?.name || 'Unknown'}){c.estimated_value ? ` - $${c.estimated_value.toFixed(2)}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {tradedCard && (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--ion-color-medium)' }}>
                      {tradedCard.name}
                      {tradedCard.estimated_value ? ` - Value: $${tradedCard.estimated_value.toFixed(2)}` : ''}
                      {tradedCard.quantity > 1 ? ` (${tradedCard.quantity} in collection)` : ''}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          <div style={fieldGroup}>
            <label style={labelStyle}>Language</label>
            <input style={inputStyle} value={form.language} onChange={(e) => setForm((f) => ({ ...f, language: e.target.value || 'English' }))} />
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Notes</label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: 'inherit' }}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>

          {selectedGame && selectedGame.custom_fields_schema.length > 0 && (
            <>
              <h3 style={{ marginTop: 16, marginBottom: 8 }}>{selectedGame.name} Fields</h3>
              {selectedGame.custom_fields_schema.map((field) => {
                if (field.type === 'text') {
                  return (
                    <div key={field.key} style={fieldGroup}>
                      <label style={labelStyle}>{field.label}</label>
                      <input style={inputStyle} value={form.custom_fields[field.key] || ''} onChange={(e) => setForm((f) => ({ ...f, custom_fields: { ...f.custom_fields, [field.key]: e.target.value } }))} />
                    </div>
                  );
                }
                if (field.type === 'number') {
                  return (
                    <div key={field.key} style={fieldGroup}>
                      <label style={labelStyle}>{field.label}</label>
                      <input style={inputStyle} type="number" value={form.custom_fields[field.key] || ''} onChange={(e) => setForm((f) => ({ ...f, custom_fields: { ...f.custom_fields, [field.key]: e.target.value ? Number(e.target.value) : null } }))} />
                    </div>
                  );
                }
                if (field.type === 'select') {
                  return (
                    <div key={field.key} style={fieldGroup}>
                      <label style={labelStyle}>{field.label}</label>
                      <select style={selectStyle} value={form.custom_fields[field.key] || ''} onChange={(e) => setForm((f) => ({ ...f, custom_fields: { ...f.custom_fields, [field.key]: e.target.value } }))}>
                        <option value="">Select...</option>
                        {field.options?.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  );
                }
                if (field.type === 'multiselect') {
                  return (
                    <div key={field.key} style={fieldGroup}>
                      <label style={labelStyle}>{field.label}</label>
                      <MultiSelectDropdown
                        options={field.options || []}
                        value={form.custom_fields[field.key] || []}
                        onChange={(selected) => setForm((f) => ({ ...f, custom_fields: { ...f.custom_fields, [field.key]: selected } }))}
                      />
                    </div>
                  );
                }
                return null;
              })}
            </>
          )}

          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={!form.name || !form.game_id || form.estimated_value === '' || createCard.isPending || updateCard.isPending}
            style={{ marginTop: 16, marginBottom: 16 }}
          >
            {(createCard.isPending || updateCard.isPending) ? <IonSpinner name="crescent" /> : (isEdit ? 'Update Card' : 'Add Card')}
          </IonButton>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default CardForm;
